/**
    * @description      : 
    * @author           : USER
    * @group            : 
    * @created          : 04/09/2025 - 18:52:43
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 04/09/2025
    * - Author          : USER
    * - Modification    : 
**/
// src/pages/about/Vision.js
import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaEye, 
  FaGraduationCap, 
  FaGlobe, 
  FaUsers,
  FaStar,
  FaHandshake,
  FaLightbulb
} from 'react-icons/fa';

const Vision = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const visionElements = [
    {
      icon: FaGraduationCap,
      title: 'Confident Leaders',
      description: 'Raising a generation of self-assured young people who believe in their ability to make a difference.'
    },
    {
      icon: FaStar,
      title: 'Articulate Communicators',
      description: 'Developing exceptional communication skills that allow ideas to be expressed clearly and persuasively.'
    },
    {
      icon: FaHandshake,
      title: 'Ethically Grounded',
      description: 'Building character and integrity that guides decision-making and leadership approaches.'
    },
    {
      icon: FaGlobe,
      title: 'Global Impact',
      description: 'Creating leaders who shape the future on both local and international stages.'
    },
    {
      icon: FaLightbulb,
      title: 'Power of Ideas',
      description: 'Harnessing the transformative potential of words and ideas to drive positive change.'
    }
  ];

  return (
    <div className="bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header Section */}
        <div className="mx-auto max-w-2xl sm:text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
              Our Vision
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-gray-600">
              Shaping the future through confident, articulate, and ethically grounded African leaders who impact both local and global stages.
            </p>
          </motion.div>
        </div>

        {/* Vision Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-16"
        >
          <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-3xl p-8 text-white text-center">
            <h3 className="text-2xl sm:text-3xl font-bold mb-4">
              "To raise a generation of confident, articulate and ethically grounded African leaders who shape the future through the power of words and ideas on both local and global stages."
            </h3>
            <div className="mt-6">
              <span className="text-2xl font-bold text-yellow-300">DEBATE. LEAD. CHANGE THE WORLD.</span>
            </div>
          </div>
        </motion.div>

        {/* Vision Elements Grid */}
        <div ref={ref} className="mx-auto mt-20 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:grid-cols-2 lg:max-w-4xl lg:grid-cols-3 xl:max-w-none">
          {visionElements.map((element, index) => {
            const Icon = element.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="flex flex-col items-start"
              >
                <div className="flex-none">
                  <div className="relative aspect-square w-20 overflow-hidden rounded-full bg-purple-100 flex items-center justify-center">
                    <Icon className="w-10 h-10 text-purple-600" />
                  </div>
                </div>
                <div className="flex-auto mt-6">
                  <h3 className="text-xl font-semibold leading-8 tracking-tight text-gray-900">
                    {element.title}
                  </h3>
                  <p className="mt-4 text-base leading-7 text-gray-600">
                    {element.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Future Impact Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-20"
        >
          <div className="bg-gray-50 rounded-3xl p-12">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">The Future We're Building</h3>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                We envision a future where African children become the catalysts for positive transformation across all sectors of society.
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="text-2xl font-bold text-purple-600 mb-2">Tomorrow's</div>
                <div className="text-gray-600 text-sm">Presidents</div>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="text-2xl font-bold text-blue-600 mb-2">Future</div>
                <div className="text-gray-600 text-sm">CEOs</div>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="text-2xl font-bold text-green-600 mb-2">Next</div>
                <div className="text-gray-600 text-sm">Innovators</div>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="text-2xl font-bold text-orange-600 mb-2">Global</div>
                <div className="text-gray-600 text-sm">Champions</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Vision;